import type { JSX } from "react";

interface Props {
  /** Real image URL. If absent, a painterly gradient placeholder is rendered. */
  src?: string;
  alt?: string;
  /** Aspect ratio as a CSS aspect-ratio value, e.g. "4 / 3". */
  aspect?: string;
  className?: string;
  /**
   * Имя-сид для генерации тональности плейсхолдера (чтобы Марина, Лиза, Катя
   * не выглядели одинаково). Пропускается, если есть реальный src.
   */
  seed?: string;
  /** Мягкий виньет-градиент поверх, добавляет «ретушь». */
  toned?: boolean;
}

interface Palette {
  a: string;
  b: string;
  base: string;
}

const DEFAULT_PALETTE: Palette = {
  a: "#e8d3b7",
  b: "#b87a55",
  base: "linear-gradient(160deg, #3d2418, #1a0f08)",
};

/** Палитры «паспарту»: тёплый, холодный, графитовый, сливовый. */
const PALETTES: readonly Palette[] = [
  DEFAULT_PALETTE,
  { a: "#c9d4e3", b: "#6a7d94", base: "linear-gradient(160deg, #2a2f36, #121417)" },
  { a: "#d9d2c4", b: "#8b7f6d", base: "linear-gradient(160deg, #2f2a22, #1a160f)" },
  { a: "#e8c9d4", b: "#8b5a7a", base: "linear-gradient(160deg, #32222a, #180e14)" },
];

function pickPalette(seed?: string): Palette {
  if (!seed) return DEFAULT_PALETTE;
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTES[h % PALETTES.length] ?? DEFAULT_PALETTE;
}

export function Portrait({
  src,
  alt = "",
  aspect = "4 / 3",
  className = "",
  seed,
  toned = true,
}: Props): JSX.Element {
  const p = pickPalette(seed);
  const bg = src
    ? undefined
    : [
        `radial-gradient(circle at 32% 38%, ${p.a} 0%, transparent 55%)`,
        `radial-gradient(circle at 70% 62%, ${p.b} 0%, transparent 60%)`,
        p.base,
      ].join(", ");

  return (
    <div
      className={`relative w-full overflow-hidden rounded-[16px] shadow-paper ${className}`}
      style={{
        aspectRatio: aspect,
        background: bg,
      }}
    >
      {src && (
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          loading="lazy"
          style={{ filter: "contrast(1.03) saturate(0.9)" }}
        />
      )}
      {toned && (
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 50% 40%, transparent 28%, rgba(21,18,14,0.28) 75%)",
          }}
        />
      )}
    </div>
  );
}
